import React from "react";
import { Hash } from "lucide-react";
import { trodoAnalyticsTheme as theme } from "features/analytics/studio/theme";
import { formatNumber } from "shared/utils/numberFormatter";
import {
  formatDurationSeconds,
  formatDurationDisplay,
} from "features/analytics/studio/utils/durationFormat";
import { DEFAULT_FUNNEL_TABLE_COLUMNS } from "features/analytics/studio/constants/funnelTableColumns";
import { renderStepKindChip } from "features/analytics/studio/utils/stepKindChip";

// Same header / cell typography as the Traces tables (dataTableStyles), with
// this grid's own alignment and column dividers kept.
const thBase =
  "h-8 px-3 align-middle text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap text-[color:var(--text-muted)]";
const thCorner = `${thBase} text-left`;
const thNum = `${thBase} text-right`;
// Step names are data (event names), so they keep their own case.
const thStepTitle = `${thBase} text-left normal-case`;
const tdProp = "px-3 align-middle text-left text-[12px] text-[color:var(--text-primary)]";
const tdNum =
  "px-3 align-middle text-right text-[12px] tabular-nums text-[color:var(--text-primary)]";
const bodyRowClass = "h-8 border-b border-[color:var(--border-subtle)] last:border-b-0";

const METRIC_DEFS = [
  { key: "conversionFromFirst", sub: "% 1st" },
  { key: "conversionFromPrevious", sub: "% prev" },
  { key: "stepCount", sub: "#" },
  { key: "avgTimeFromFirst", sub: "Avg →N" },
  { key: "avgTimeFromPrevious", sub: "Avg Δ" },
];

/** Funnel conversion rates (0–100): one decimal place + % */
function formatConversionPct(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  return `${(Math.round(n * 10) / 10).toFixed(1)}%`;
}

export default function FunnelDataTable({
  steps = [],
  measuring = "conversion_rate",
  breakdowns = [],
  tableColumns: tableColumnsProp,
  /** Smaller type and padding for board cards */
  compact = false,
}) {
  const tableColumns = { ...DEFAULT_FUNNEL_TABLE_COLUMNS, ...(tableColumnsProp || {}) };
  const enabledMetricKeys = METRIC_DEFS.filter((m) => tableColumns[m.key]).map((m) => m.key);
  const ttcExtraCol = measuring === "time_to_convert";

  const overallConversion =
    steps.length > 1 && steps[0]?.count > 0
      ? ((steps[steps.length - 1]?.count || 0) / steps[0].count) * 100
      : steps.length === 1
        ? 100
        : 0;

  const finalOverallConversion =
    steps.length > 0 && steps[0]?.overallConversion !== undefined
      ? steps[0].overallConversion
      : overallConversion;

  const getStep1DisplayValue = (step) => {
    const count = step.count || 0;
    const value = step.value;
    const timeToConvert = step.timeToConvert || (measuring === "time_to_convert" ? step.value : null);
    switch (measuring) {
      case "unique_users":
        return formatNumber(value !== undefined ? value : count);
      case "total_conversions":
      case "total_sessions":
      case "property_sum":
        return formatNumber(value !== undefined ? value : 0);
      case "time_to_convert":
        return formatDurationDisplay(timeToConvert);
      case "conversion_rate":
      default:
        return formatNumber(count);
    }
  };

  const renderMetricValue = (key, stepOrInfo) => {
    const conversion = stepOrInfo.conversion ?? 0;
    const cf = stepOrInfo.conversionFromFirst;
    const rawCount = stepOrInfo.count ?? 0;
    switch (key) {
      case "conversionFromFirst":
        return formatConversionPct(cf != null ? cf : 0);
      case "conversionFromPrevious":
        return formatConversionPct(conversion);
      case "stepCount":
        return formatNumber(rawCount);
      case "avgTimeFromFirst":
        return formatDurationDisplay(
          stepOrInfo.avgTimeFromFirst ?? stepOrInfo.timeToConvert
        );
      case "avgTimeFromPrevious":
        return formatDurationDisplay(stepOrInfo.avgTimeFromPrevious);
      default:
        return "—";
    }
  };

  const colspanStepN = (index) => {
    if (index === 0) return 1;
    return (ttcExtraCol ? 1 : 0) + enabledMetricKeys.length;
  };

  const borderR = { borderRight: `1px solid ${theme.border}` };

  const rightBorderLast = (idx, len) =>
    idx < len - 1 ? { borderRight: `1px solid ${theme.border}` } : undefined;

  return (
    <div
      className="w-full overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface"
    >
      <div className="overflow-x-auto">
      <table className={`w-full border-collapse bg-surface text-ink ${compact ? "min-w-0" : "min-w-[520px]"}`}>
        <thead>
          <tr className="h-8 border-b border-[color:var(--border-default)]">
            <th rowSpan={2} className={thCorner} style={{ ...borderR, width: "28%", maxWidth: "14rem" }}>
              Property
            </th>
            <th rowSpan={2} className={thNum} style={{ ...borderR, width: "6.5rem" }}>
              Total conv.
            </th>
            {steps.map((step, index) => {
              const stepLabel = step.label || step.eventName || `Step ${index + 1}`;
              return (
                <React.Fragment key={step.id || index}>
                  {index === 0 && (
                    <th
                      colSpan={1}
                      className={`${thStepTitle} min-w-0 max-w-[12rem]`}
                      style={rightBorderLast(index, steps.length)}
                    >
                      <span className="text-ink-disabled">{index + 1}</span>
                      <span className="ml-1.5 truncate">{stepLabel}</span>
                      {renderStepKindChip(step)}
                    </th>
                  )}
                  {index > 0 && (
                    <th colSpan={colspanStepN(index)} className={`${thStepTitle} min-w-0`} style={rightBorderLast(index, steps.length)}>
                      <span className="text-ink-disabled">{index + 1}</span>
                      <span className="ml-1.5 truncate">{stepLabel}</span>
                      {renderStepKindChip(step)}
                    </th>
                  )}
                </React.Fragment>
              );
            })}
          </tr>
          <tr className="h-8 border-b border-[color:var(--border-default)]">
            {steps.map((step, index) => (
              <React.Fragment key={step.id || index}>
                {index === 0 && (
                  <th className={thNum} style={rightBorderLast(index, steps.length)}>
                    <span className="inline-flex items-center justify-end gap-0.5 text-ink-disabled">
                      <Hash className="h-2.5 w-2.5" strokeWidth={2.5} aria-hidden />
                    </span>
                  </th>
                )}
                {index > 0 && (
                  <>
                    {ttcExtraCol && (
                      <th
                        className={`${thNum} normal-case`}
                        style={
                          enabledMetricKeys.length === 0 ? rightBorderLast(index, steps.length) : undefined
                        }
                      >
                        Step Δt
                      </th>
                    )}
                    {enabledMetricKeys.map((k, mi) => (
                      <th
                        key={k}
                        className={`${thNum} normal-case`}
                        style={
                          mi === enabledMetricKeys.length - 1 ? rightBorderLast(index, steps.length) : undefined
                        }
                      >
                        {METRIC_DEFS.find((m) => m.key === k)?.sub || k}
                      </th>
                    ))}
                  </>
                )}
              </React.Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr className={bodyRowClass}>
            <td className={tdProp} style={borderR}>
              <div className="flex min-w-0 items-center gap-1.5">
                <div
                  className="h-2 w-2 shrink-0 rounded-sm"
                  style={{ backgroundColor: theme.primaryLight }}
                />
                <span className="truncate font-medium">Overall</span>
              </div>
            </td>

            <td className={`${tdNum} font-medium`} style={borderR}>
              {formatConversionPct(finalOverallConversion)}
            </td>

            {steps.map((step, index) => {
              const timeToConvert =
                step.timeToConvert || (measuring === "time_to_convert" ? step.value : null);
              return (
                <React.Fragment key={step.id || index}>
                  {index === 0 && (
                    <td className={tdNum} style={rightBorderLast(index, steps.length)}>
                      {getStep1DisplayValue(step)}
                    </td>
                  )}

                  {index > 0 && (
                    <>
                      {ttcExtraCol && (
                        <td
                          className={tdNum}
                          style={
                            enabledMetricKeys.length === 0 ? rightBorderLast(index, steps.length) : undefined
                          }
                        >
                          {formatDurationDisplay(timeToConvert)}
                        </td>
                      )}
                      {enabledMetricKeys.map((k, mi) => (
                        <td
                          key={k}
                          className={tdNum}
                          style={mi === enabledMetricKeys.length - 1 ? rightBorderLast(index, steps.length) : undefined}
                        >
                          {renderMetricValue(k, step)}
                        </td>
                      ))}
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </tr>

          {breakdowns &&
            breakdowns.length > 0 &&
            breakdowns.map((breakdown, breakdownIndex) => {
              const breakdownValue =
                breakdown.breakdownValue || breakdown.breakdownKey || "Unknown";
              const overallConversionRow = breakdown.overallConversion || 0;
              const stepData = breakdown.steps || {};

              const colors = [
                theme.primaryLight,
                "var(--feedback-info)",
                "#10b981",
                "var(--feedback-warning)",
                "var(--feedback-error)",
                "#8b5cf6",
                "#06b6d4",
                "#84cc16",
              ];
              const breakdownColor = colors[breakdownIndex % colors.length];

              return (
                <tr key={breakdownIndex} className={bodyRowClass}>
                  <td className={tdProp} style={borderR}>
                    <div className="flex min-w-0 items-center gap-1.5">
                      <div
                        className="h-2 w-2 shrink-0 rounded-sm"
                        style={{ backgroundColor: breakdownColor }}
                      />
                      <span className="truncate">{breakdownValue}</span>
                    </div>
                  </td>

                  <td className={tdNum} style={borderR}>
                    {formatConversionPct(overallConversionRow)}
                  </td>

                  {steps.map((step, stepIndex) => {
                    const stepNum = stepIndex + 1;
                    const stepInfo = stepData[`step${stepNum}`] || {};
                    const count = stepInfo.count || 0;
                    const value = stepInfo.value;
                    const conversionRate =
                      stepInfo.conversionRate || (stepIndex === 0 ? 100.0 : 0.0);
                    const timeToConvert =
                      stepInfo.timeToConvert || (measuring === "time_to_convert" ? value : null);

                    const merged = {
                      ...stepInfo,
                      count,
                      value,
                      conversion: conversionRate,
                      timeToConvert,
                    };

                    return (
                      <React.Fragment key={step.id || stepIndex}>
                        {stepIndex === 0 && (
                          <td className={tdNum} style={rightBorderLast(stepIndex, steps.length)}>
                            {getStep1DisplayValue(merged)}
                          </td>
                        )}

                        {stepIndex > 0 && (
                          <>
                            {ttcExtraCol && (
                              <td
                                className={tdNum}
                                style={
                                  enabledMetricKeys.length === 0
                                    ? rightBorderLast(stepIndex, steps.length)
                                    : undefined
                                }
                              >
                                {formatDurationDisplay(timeToConvert)}
                              </td>
                            )}
                            {enabledMetricKeys.map((k, mi) => (
                              <td
                                key={k}
                                className={tdNum}
                                style={
                                  mi === enabledMetricKeys.length - 1
                                    ? rightBorderLast(stepIndex, steps.length)
                                    : undefined
                                }
                              >
                                {renderMetricValue(k, merged)}
                              </td>
                            ))}
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tr>
              );
            })}
        </tbody>
      </table>
      </div>
    </div>
  );
}
